<template>
    <div class="gap-md flex items-center">
        <!-- self-end: the trigger sits in the Graphs toolbar next to label+input
             pairs, so it has to bottom-align with the input row rather than
             centre against the taller label+input box. The legacy btn-alt
             utility carried this as a hidden `mt-auto`, which was dropped when
             ITDEVELOP-2722 moved the trigger onto ui/Button (ITDEVELOP-3191). -->
        <Button variant="tertiary" type="button" class="pdf-hidden self-end" @click="openExportModal">Export</Button>
    </div>

    <!-- ITDEVELOP-2722: ui/Dialog replaces the fixed-div scrim shell; width/height
         overrides preserve this modal's pre-migration responsive footprint. -->
    <Dialog v-model:open="showExportModal">
        <DialogContent class="max-h-[90vh] min-h-[35rem] w-[95%] max-w-none sm:w-3/4 xl:w-1/2 2xl:w-1/3">
            <div class="gap-xs flex w-full flex-col">
                <DialogTitle class="text-2xl">Exporting {{ props.dataType }} data</DialogTitle>
                <p class="text-(--text-muted)">Zones: {{ props.zones.join(', ') }}</p>
            </div>

            <div class="gap-md grid w-full grid-cols-2">
                <div class="gap-xs flex flex-col">
                    <label for="startDate" class="mr-sm">Start Date</label>
                    <div class="input-wrapper flex items-center">
                        <DatePicker
                            id="startDate"
                            ref="datePickerRef"
                            v-model="exportInfo.startDate"
                            :base-z-index="20000"
                            :manual-input="true"
                            date-format="dd.M.yy"
                            placeholder="Select a start date"
                            class="datepicker"
                            show-time
                            hour-format="24"
                            :max-date="new Date(Date.now() - 20 * 60 * 1000)"
                            :class="{ 'text-(--text-disabled)': !exportInfo.startDate }"
                        />
                        <i class="i-calendar pr-sm mr-sm size-5" />
                    </div>
                </div>

                <div class="gap-xs flex flex-col">
                    <label for="time-range" class="mr-sm">End Date</label>
                    <div class="input-wrapper flex items-center">
                        <DatePicker
                            id="time-range"
                            v-model="exportInfo.endDate"
                            :manual-input="true"
                            date-format="dd.M.yy"
                            placeholder="Select an end date"
                            class="datepicker"
                            show-time
                            hour-format="24"
                            :max-date="new Date()"
                            :class="{ 'text-(--text-disabled)': !endDate }"
                        />
                        <i class="i-calendar pr-sm mr-sm size-5" />
                    </div>
                </div>

                <InputSelect
                    :options="props.exportTypeOptions"
                    :value="[exportInfo.exportType]"
                    :unique-key="1"
                    label="Export type"
                    class="w-[13rem]"
                    @update="setExportField('exportType', $event[0])"
                />

                <div class="gap-xs flex">
                    <div class="gap-xs flex flex-col">
                        <label for="interval">Interval</label>
                        <div class="input-wrapper gap-xs flex flex-col">
                            <input
                                id="interval"
                                v-model="exportInfo.interval"
                                type="number"
                                :disabled="dataType === 'energy'"
                                :min="exportInfo.intervalUnit[0].value === 'm' ? 5 : 1"
                                class="w-[7rem]"
                            />
                        </div>
                    </div>
                    <InputSelect
                        :options="[
                            { label: 'minutes', value: 'm' },
                            { label: 'hours', value: 'h' },
                            { label: 'days', value: 'd' },
                        ]"
                        :value="exportInfo.intervalUnit"
                        :unique-key="2"
                        :disabled="dataType === 'energy'"
                        label="Unit"
                        class="interval"
                        @update="setExportField('intervalUnit', $event)"
                    />
                </div>

                <div class="gap-xs flex">
                    <InputSelect
                        :options="props.aggregateOptions"
                        :value="exportInfo.aggregate"
                        :unique-key="3"
                        label="Aggregate"
                        class="w-[13rem]"
                        @update="setExportField('aggregate', $event)"
                    />
                </div>
            </div>

            <DialogFooter class="mt-auto">
                <Button @click="handleExport">
                    <span v-if="!props.exportingData"> Export </span>
                    <i v-else class="i-spinner-white animate-spin" />
                </Button>

                <Button variant="secondary" @click="closeExportModal">Cancel</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import { DatePicker } from 'primevue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import InputSelect from '@/components/support/input-select.vue';
import type { ExportInfo } from '@/stores/store-interfaces';
import { useOrganizationStore } from '@/stores/organization';

const showExportModal = ref<boolean>(false);

function openExportModal() {
    showExportModal.value = true;
}

function closeExportModal() {
    showExportModal.value = false;
}

const props = defineProps<{
    exportTypeOptions: { label: string; value: string }[];
    aggregateOptions: { label: string; value: string }[];
    selectedAggregate: { label: string; value: string }[];
    zones: string[];
    startDate: Date;
    endDate: Date;
    dataType: string;
    exportingData: boolean;
}>();

const emit = defineEmits<{
    (e: 'export', exportInfo: ExportInfo): void;
}>();

const exportInfo = ref<ExportInfo>({
    exportType: props.exportTypeOptions[0],
    dataPointsCount: 500,
    interval: 5,
    intervalUnit: [{ label: 'minutes', value: 'm' }],
    startDate: props.startDate,
    endDate: props.endDate,
    aggregate: [props.aggregateOptions[0]],
});

function setExportField<K extends keyof ExportInfo>(key: K, value: ExportInfo[K]) {
    exportInfo.value[key] = value;
}

const handleExport = () => {
    if (exportInfo.value.interval < 5 && exportInfo.value.intervalUnit[0].value === 'm') {
        useOrganizationStore().setToastMessage('Interval must be at least 5 minutes.', 'warning');
        return;
    }
    emit('export', exportInfo.value);
};

watch(
    () => showExportModal.value,
    (val) => {
        if (val) {
            exportInfo.value.startDate = props.startDate;
            exportInfo.value.endDate = props.endDate;
        }

        if (props.dataType === 'energy') {
            exportInfo.value.interval = 1;
            exportInfo.value.intervalUnit = [{ label: 'days', value: 'd' }];
            exportInfo.value.aggregate =
                props.selectedAggregate.length > 0 ? props.selectedAggregate : [props.aggregateOptions[0]];
        } else if (props.dataType === 'pue') {
            exportInfo.value.interval = 5;
            exportInfo.value.intervalUnit = [{ label: 'minutes', value: 'm' }];
            exportInfo.value.aggregate =
                props.selectedAggregate.length > 0 ? props.selectedAggregate : [props.aggregateOptions[0]];
        } else {
            // Reset to defaults for other data types if needed
            exportInfo.value.interval = 5;
            exportInfo.value.intervalUnit = [{ label: 'minutes', value: 'm' }];
            // Default to whatever is passed (usually matches view) or first available
            exportInfo.value.aggregate =
                props.selectedAggregate.length > 0 ? props.selectedAggregate : [props.aggregateOptions[0]];
        }
    },
);
</script>

<style>
@reference '@/assets/styles/default.css';

::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
}

.interval button {
    @apply min-w-0;
}
</style>
